import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import { useLanguage } from "../i18n";
import { useSEO } from "../seo";

const COPY = {
  it: {
    title: "Termini e Condizioni | OGStay",
    desc: "Termini e condizioni di utilizzo del sito OGStay.",
    updated: "Ultimo aggiornamento: settembre 2026",
    h: "Termini e Condizioni",
    notice:
      "Il testo legale definitivo di questa pagina è in fase di completamento e sarà pubblicato prima della messa online definitiva del sito.",
    s1h: "Prenotazioni",
    s1p:
      "Le prenotazioni dei soggiorni avvengono tramite il portale di prenotazione Kross Booking (ogstay.kross.travel). Condizioni di soggiorno, cancellazione e pagamento sono quelle mostrate e accettate in fase di prenotazione sul portale.",
    s2h: "Contenuti del sito",
    s2p:
      "Le informazioni su strutture, esperienze e servizi sono fornite in buona fede e possono variare. Per conferme e dettagli aggiornati contattaci direttamente.",
    s3h: "Contatti",
    s3p: "Per qualsiasi domanda sui termini di utilizzo del sito puoi scriverci a info@ogstay.com.",
    cta: "Scrivici",
    privacyLink: "Leggi la Privacy Policy",
  },
  en: {
    title: "Terms & Conditions | OGStay",
    desc: "Terms and conditions of use of the OGStay website.",
    updated: "Last updated: September 2026",
    h: "Terms & Conditions",
    notice:
      "The final legal text of this page is being completed and will be published before the site goes definitively live.",
    s1h: "Bookings",
    s1p:
      "Stays are booked through the Kross Booking portal (ogstay.kross.travel). Stay, cancellation and payment conditions are those shown and accepted during booking on the portal.",
    s2h: "Site content",
    s2p:
      "Information about properties, experiences and services is provided in good faith and may change. For confirmations and updated details, contact us directly.",
    s3h: "Contact",
    s3p: "For any question about these terms of use, write to us at info@ogstay.com.",
    cta: "Write to us",
    privacyLink: "Read the Privacy Policy",
  },
};

export default function TermsPage() {
  const { lang } = useLanguage();
  const c = COPY[lang];
  useSEO(c.title, c.desc);

  return (
    <div data-testid="terms-page" className="bg-warm px-5 pb-24 pt-36 sm:px-8 sm:pt-44">
      <div className="mx-auto max-w-3xl">
        <h1 data-testid="terms-title" className="font-display text-4xl font-semibold tracking-tight text-navy sm:text-5xl">
          {c.h}
        </h1>
        <p className="mt-3 text-xs font-bold uppercase tracking-[0.15em] text-mute">{c.updated}</p>

        <p data-testid="terms-notice" className="mt-8 rounded-2xl bg-sand px-6 py-5 text-sm font-semibold leading-relaxed text-navy ring-1 ring-navy/10">
          {c.notice}
        </p>

        <div className="mt-12 space-y-10">
          {[
            { h: c.s1h, p: c.s1p },
            { h: c.s2h, p: c.s2p },
            { h: c.s3h, p: c.s3p },
          ].map((s, i) => (
            <section key={s.h} data-testid={`terms-section-${i}`}>
              <h2 className="font-display text-2xl font-semibold text-navy">{s.h}</h2>
              <p className="mt-4 border-l-2 border-sky/40 pl-5 text-sm leading-relaxed text-ink/80">{s.p}</p>
            </section>
          ))}
        </div>

        <div className="mt-14 flex flex-wrap items-center gap-4">
          <a data-testid="terms-contact-cta" href="mailto:info@ogstay.com" className="btn-dark !px-6 !py-3 text-[13px]">
            <Mail className="h-4 w-4" />
            {c.cta}
          </a>
          <Link data-testid="terms-to-privacy-link" to="/privacy-policy" className="text-sm font-bold text-navy underline decoration-sky decoration-2 underline-offset-4 transition-colors hover:text-med">
            {c.privacyLink}
          </Link>
        </div>
      </div>
    </div>
  );
}
